import { AccountSummaryCard } from '@/components/budget-nav/account-summary';
import { AccountsButton } from '@/components/budget-nav/accounts-button';
import { BudgetNav } from '@/components/budget-nav/budget-nav';
import { MainErrorFallback } from '@/components/errors/main';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
} from '@/components/ui/sidebar';
import { Spinner } from '@/components/ui/spinner';
import { Toaster } from '@/components/ui/toast/toaster';
import { ReactNode, Suspense, useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router-dom';
import { BudgetSwitcher } from '@/components/budget-switcher/budget-switcher';
import {
  computeBudgetSummary,
  useGetBudgetGraphApi,
  useGetUserApi,
  useSetBudgetCurrency,
  useSetBudgetId,
  useSetBudgetLocale,
  useSetBudgetSummary,
  useTimeZone,
} from '@liquidbudget/common';
import { useInitializeDate } from '@/pages/budget/budget/budget-page-store';

interface BudgetLayoutProps {
  children: ReactNode;
}

export const BudgetLayout = ({ children }: BudgetLayoutProps) => {
  const params = useParams();
  const budgetId = params.budgetId as string;
  const setBudgetId = useSetBudgetId();
  const setBudgetLocale = useSetBudgetLocale();
  const setBudgetCurrency = useSetBudgetCurrency();
  const setBudgetSummary = useSetBudgetSummary();
  const { data: user } = useGetUserApi();
  const initializeDate = useInitializeDate();
  const timeZone = useTimeZone();

  useEffect(() => {
    if (budgetId) {
      setBudgetId(budgetId);
    }
  }, [budgetId, setBudgetId]);

  const { isLoading, error, budget, accounts, transactions, buckets } = useGetBudgetGraphApi(budgetId);

  useEffect(() => {
    if (budget) {
      setBudgetCurrency(budget.currency);
      setBudgetLocale(budget.locale);
    }
  }, [budget, setBudgetCurrency, setBudgetLocale]);

  const budgetSummary = useMemo(() => {
    if (accounts && transactions && buckets) {
      return computeBudgetSummary(timeZone, accounts, transactions, buckets);
    }
    return null;
  }, [timeZone, accounts, transactions, buckets]);

  useEffect(() => {
    if (budgetSummary) {
      setBudgetSummary(budgetSummary);
    }
  }, [budgetSummary, setBudgetSummary]);

  useEffect(() => {
    if (user?.timeZone) {
      initializeDate(user.timeZone);
    }
  }, [user, initializeDate]);

  if (isLoading) {
    return (
      <div className="flex size-full items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (error) {
    return <div>Something went wrong: {(error as Error).message}</div>;
  }

  return (
    <Suspense
      fallback={
        <div className="flex size-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      }
    >
      <ErrorBoundary key={location.pathname} fallback={<MainErrorFallback />}>
        <SidebarProvider>
          <Sidebar>
            <SidebarHeader className="flex h-14 items-center px-4 border-b border-sidebar-border">
              <BudgetSwitcher budget={budget} />
            </SidebarHeader>
            <SidebarContent>
              <BudgetNav />
            </SidebarContent>
            <SidebarFooter>
              <div className="p-4 pt-2 pb-2">
                <AccountSummaryCard />
                <AccountsButton />
              </div>
            </SidebarFooter>
          </Sidebar>
          <SidebarInset>{children}</SidebarInset>
        </SidebarProvider>
        <Toaster />
      </ErrorBoundary>
    </Suspense>
  );
};
